import React, { useState } from 'react';
import { Clock } from 'lucide-react';
import { useSocialPosts } from '../hooks/useSocialPosts';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from './LoadingSpinner';

export default function SocialFeed() {
  const [newPost, setNewPost] = useState('');
  const [isPosting, setIsPosting] = useState(false);
  
  const { posts, loading, createPost } = useSocialPosts();
  const { profile } = useAuth();

  const formatTimeAgo = (timestamp: string) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffInHours = Math.floor((now.getTime() - time.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours === 1) return '1 hour ago';
    if (diffInHours < 24) return `${diffInHours} hours ago`;
    return `${Math.floor(diffInHours / 24)} days ago`;
  };

  const handleNewPost = async () => {
    if (!newPost.trim() || isPosting) return;

    setIsPosting(true);
    
    try {
      await createPost(newPost);
      setNewPost('');
    } catch (error) {
      console.error('Error creating post:', error);
      alert('Failed to create post. Please try again.');
    } finally {
      setIsPosting(false);
    }
  };

  if (loading || !profile) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-3xl font-bold text-gray-900 mb-4">Community Feed</h2>
        <p className="text-gray-600">
          Share your eco-journey and get inspired by fellow green warriors
        </p>
      </div>

      {/* New Post */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <div className="flex items-start space-x-4">
          <img
            src={profile.avatar_url}
            alt={profile.username}
            className="w-12 h-12 rounded-full"
          />
          <div className="flex-1">
            <textarea
              value={newPost}
              onChange={(e) => setNewPost(e.target.value)}
              placeholder="Share your latest eco-action or inspire others..."
              className="w-full p-3 border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              rows={3}
            />
            
            <div className="flex items-center justify-end mt-4">
              <button
                onClick={handleNewPost}
                disabled={!newPost.trim() || isPosting}
                className="bg-emerald-500 hover:bg-emerald-600 disabled:bg-gray-300 text-white px-6 py-2 rounded-lg font-medium transition-colors"
              >
                {isPosting ? 'Sharing...' : 'Share'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Posts Feed */}
      <div className="space-y-6">
        {posts.length > 0 ? (
          posts.map((post) => (
            <div key={post.id} className="bg-white rounded-2xl border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
              {/* Post Header */}
              <div className="p-6 pb-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <img
                      src={post.profiles.avatar_url}
                      alt={post.profiles.username}
                      className="w-12 h-12 rounded-full"
                    />
                    <div>
                      <h4 className="font-semibold text-gray-900">{post.profiles.username}</h4>
                      <div className="flex items-center space-x-2 text-sm text-gray-500">
                        <Clock className="h-4 w-4" />
                        <span>{formatTimeAgo(post.created_at)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Mission Badge */}
                {post.missions && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-4">
                    <div className="flex items-center space-x-2">
                      <div className="bg-emerald-500 p-1 rounded">
                        <div className="w-2 h-2 bg-white rounded-full"></div>
                      </div>
                      <span className="text-sm font-medium text-emerald-700">
                        Completed mission: {post.missions.title}
                      </span>
                      <span className="bg-emerald-200 text-emerald-800 px-2 py-0.5 rounded-full text-xs font-medium">
                        +{post.missions.points} pts
                      </span>
                    </div>
                  </div>
                )}

                {/* Post Content */}
                <p className="text-gray-900 leading-relaxed">{post.content}</p>
              </div>

              {/* Post Image */}
              {post.image_url && (
                <div className="px-6 pb-4">
                  <img
                    src={post.image_url}
                    alt="Post content"
                    className="w-full h-64 object-cover rounded-lg"
                  />
                </div>
              )}

              {/* Post Footer - Simplified */}
              <div className="px-6 py-4 border-t border-gray-100">
                <div className="text-center">
                  <span className="text-sm text-gray-500">
                    Shared with the eco-community 🌱
                  </span>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
            <p className="text-gray-600">No posts yet. Be the first to share your eco journey!</p>
          </div>
        )}
      </div>
    </div>
  );
}